// EmployeeCreateDialog.js
// Multi-step dialog for creating new employees

import React, { useState, useEffect, useRef } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormHelperText,
  InputAdornment,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import MobileDialog from './MobileDialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createEmployee, getDepartments, getEmployeeTypes, createDepartment } from '../../services/employeeService';

const steps = ['פרטים אישיים', 'פרטי העסקה', 'שכר', 'אישור'];

const formatDateLocal = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const EmployeeCreateDialog = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorRef = useRef(null);

  // State for inline department creation
  const [showNewDeptInput, setShowNewDeptInput] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [isCreatingDept, setIsCreatingDept] = useState(false);

  // State for controlling department dropdown
  const [deptSelectOpen, setDeptSelectOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    FirstName: '',
    LastName: '',
    IDNumber: '',
    EmployeeNumber: '',
    PhoneNumber: '',
    Email: '',
    Address: '',
    DepartmentIDs: [],
    EmployeeTypeID: '',
    HireDate: new Date(),
    Status: 'active',
    WageType: 'monthly',
    WageAmount: '',
    Notes: '',
  });

  // Fetch departments and employee types
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: getDepartments,
    enabled: open,
  });

  const { data: employeeTypes = [] } = useQuery({
    queryKey: ['employeeTypes'],
    queryFn: getEmployeeTypes,
    enabled: open,
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setFormData({
        FirstName: '',
        LastName: '',
        IDNumber: '',
        EmployeeNumber: '',
        PhoneNumber: '',
        Email: '',
        Address: '',
        DepartmentIDs: [],
        EmployeeTypeID: '',
        HireDate: new Date(),
        Status: 'active',
        WageType: 'monthly',
        WageAmount: '',
        Notes: '',
      });
      setError('');
      setIsSubmitting(false);
      setShowNewDeptInput(false);
      setNewDeptName('');
    }
  }, [open]);

  // Mutation for creating department on the fly
  const createDeptMutation = useMutation({
    mutationFn: createDepartment,
    onSuccess: async (newDept) => {
      // Validate response has DepartmentID
      if (!newDept?.DepartmentID) {
        setError('שגיאה ביצירת מחלקה - לא התקבל מזהה');
        setIsCreatingDept(false);
        return;
      }
      // Await invalidation to ensure departments list is updated
      await queryClient.invalidateQueries({ queryKey: ['departments'] });
      // Add the new department to selection
      setFormData((prev) => ({
        ...prev,
        DepartmentIDs: [...prev.DepartmentIDs, newDept.DepartmentID],
      }));
      setShowNewDeptInput(false);
      setNewDeptName('');
      setIsCreatingDept(false);
    },
    onError: (err) => {
      setError(err?.response?.data?.error || 'שגיאה ביצירת מחלקה');
      setIsCreatingDept(false);
    },
  });

  const handleCreateDepartment = () => {
    if (!newDeptName.trim()) return;
    setIsCreatingDept(true);
    createDeptMutation.mutate({ Name: newDeptName.trim() });
  };

  // Scroll to error
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const createMutation = useMutation({
    mutationFn: createEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      onClose();
    },
    onError: (err) => {
      setError(err?.response?.data?.error || 'שגיאה ביצירת עובד');
      setIsSubmitting(false);
    },
  });

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleDateChange = (field) => (date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));
  };

  const validateStep = (step) => {
    setError('');

    switch (step) {
      case 0: // Personal info
        if (!formData.FirstName.trim()) {
          setError('שם פרטי נדרש');
          return false;
        }
        if (!formData.LastName.trim()) {
          setError('שם משפחה נדרש');
          return false;
        }
        return true;

      case 1: // Employment info
        if (!formData.HireDate) {
          setError('תאריך תחילת עבודה נדרש');
          return false;
        }
        return true;

      case 2: // Wage info
        if (!formData.WageAmount || parseFloat(formData.WageAmount) <= 0) {
          setError('סכום שכר נדרש');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setError('');
    setIsSubmitting(true);

    const data = {
      FirstName: formData.FirstName.trim(),
      LastName: formData.LastName.trim(),
      IDNumber: formData.IDNumber.trim() || null,
      EmployeeNumber: formData.EmployeeNumber.trim() || null,
      PhoneNumber: formData.PhoneNumber.trim() || null,
      Email: formData.Email.trim() || null,
      Address: formData.Address.trim() || null,
      DepartmentIDs: formData.DepartmentIDs.filter((id) => id != null),
      EmployeeTypeID: formData.EmployeeTypeID || null,
      HireDate: formatDateLocal(formData.HireDate),
      Status: formData.Status,
      WageType: formData.WageType,
      WageAmount: parseFloat(formData.WageAmount),
      Notes: formData.Notes.trim() || null,
    };

    createMutation.mutate(data);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="שם פרטי"
                value={formData.FirstName}
                onChange={handleChange('FirstName')}
                fullWidth
                required
                autoFocus
              />
              <TextField
                label="שם משפחה"
                value={formData.LastName}
                onChange={handleChange('LastName')}
                fullWidth
                required
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="תעודת זהות"
                value={formData.IDNumber}
                onChange={handleChange('IDNumber')}
                fullWidth
              />
              <TextField
                label="מספר עובד"
                value={formData.EmployeeNumber}
                onChange={handleChange('EmployeeNumber')}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="טלפון"
                value={formData.PhoneNumber}
                onChange={handleChange('PhoneNumber')}
                fullWidth
              />
              <TextField
                label="אימייל"
                type="email"
                value={formData.Email}
                onChange={handleChange('Email')}
                fullWidth
              />
            </Box>
            <TextField
              label="כתובת"
              value={formData.Address}
              onChange={handleChange('Address')}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>מחלקות</InputLabel>
              <Select
                multiple
                open={deptSelectOpen}
                onOpen={() => setDeptSelectOpen(true)}
                onClose={() => setDeptSelectOpen(false)}
                value={formData.DepartmentIDs}
                onChange={(e) => {
                  // Filter out null/undefined values (from "Add new" menu item)
                  const validIds = e.target.value.filter((id) => id != null);
                  setFormData((prev) => ({ ...prev, DepartmentIDs: validIds }));
                  setDeptSelectOpen(false);
                }}
                input={<OutlinedInput label="מחלקות" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const dept = departments.find((d) => d.DepartmentID === id);
                      return <Chip key={id} label={dept?.Name || id} size="small" />;
                    })}
                  </Box>
                )}
              >
                {departments.map((dept) => (
                  <MenuItem key={dept.DepartmentID} value={dept.DepartmentID}>
                    {dept.Name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowNewDeptInput(true);
                    setDeptSelectOpen(false);
                  }}
                  sx={{ color: 'primary.main' }}
                >
                  <ListItemIcon sx={{ color: 'primary.main', minWidth: 36 }}>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>הוסף מחלקה חדשה</ListItemText>
                </MenuItem>
              </Select>
              <FormHelperText>ניתן לבחור מספר מחלקות</FormHelperText>
            </FormControl>

            {/* Inline department creation */}
            {showNewDeptInput && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label="שם מחלקה חדשה"
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                  size="small"
                  fullWidth
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateDepartment();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCreateDepartment}
                  disabled={isCreatingDept || !newDeptName.trim()}
                >
                  {isCreatingDept ? <CircularProgress size={20} /> : 'צור'}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setShowNewDeptInput(false);
                    setNewDeptName('');
                  }}
                >
                  ביטול
                </Button>
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>סוג עובד</InputLabel>
              <Select
                value={formData.EmployeeTypeID}
                onChange={handleChange('EmployeeTypeID')}
                label="סוג עובד"
              >
                <MenuItem value="">
                  <em>ללא</em>
                </MenuItem>
                {employeeTypes.map((type) => (
                  <MenuItem key={type.TypeID} value={type.TypeID}>
                    {type.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
              <DatePicker
                label="תאריך תחילת עבודה"
                value={formData.HireDate}
                onChange={handleDateChange('HireDate')}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    sx: { '& .MuiInputBase-root': { direction: 'ltr' } },
                  },
                }}
              />
            </LocalizationProvider>

            <FormControl fullWidth>
              <InputLabel>סטטוס</InputLabel>
              <Select
                value={formData.Status}
                onChange={handleChange('Status')}
                label="סטטוס"
              >
                <MenuItem value="active">פעיל</MenuItem>
                <MenuItem value="inactive">לא פעיל</MenuItem>
                <MenuItem value="terminated">סיים עבודה</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>סוג שכר</InputLabel>
              <Select
                value={formData.WageType}
                onChange={handleChange('WageType')}
                label="סוג שכר"
              >
                <MenuItem value="monthly">חודשי</MenuItem>
                <MenuItem value="hourly">שעתי</MenuItem>
                <MenuItem value="daily">יומי</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={formData.WageType === 'hourly' ? 'שכר לשעה' : formData.WageType === 'daily' ? 'שכר ליום' : 'שכר חודשי'}
              type="number"
              value={formData.WageAmount}
              onChange={handleChange('WageAmount')}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">₪</InputAdornment>,
              }}
            />

            <TextField
              label="הערות"
              value={formData.Notes}
              onChange={handleChange('Notes')}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
            <Typography variant="h6">סיכום פרטי העובד</Typography>
            <Divider />

            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, overflow: 'hidden' }}>
              <Typography variant="body1" color="text.secondary">שם מלא:</Typography>
              <Typography variant="body1">{formData.FirstName} {formData.LastName}</Typography>

              {formData.IDNumber && (
                <>
                  <Typography variant="body1" color="text.secondary">ת.ז.:</Typography>
                  <Typography variant="body1">{formData.IDNumber}</Typography>
                </>
              )}

              {formData.EmployeeNumber && (
                <>
                  <Typography variant="body1" color="text.secondary">מספר עובד:</Typography>
                  <Typography variant="body1">{formData.EmployeeNumber}</Typography>
                </>
              )}

              {formData.PhoneNumber && (
                <>
                  <Typography variant="body1" color="text.secondary">טלפון:</Typography>
                  <Typography variant="body1">{formData.PhoneNumber}</Typography>
                </>
              )}

              {formData.Email && (
                <>
                  <Typography variant="body1" color="text.secondary">אימייל:</Typography>
                  <Typography variant="body1">{formData.Email}</Typography>
                </>
              )}

              <Typography variant="body1" color="text.secondary">תאריך התחלה:</Typography>
              <Typography variant="body1">
                {formData.HireDate ? formData.HireDate.toLocaleDateString('he-IL') : '-'}
              </Typography>

              <Typography variant="body1" color="text.secondary">מחלקות:</Typography>
              <Typography variant="body1">
                {formData.DepartmentIDs.length > 0
                  ? formData.DepartmentIDs.map((id) => {
                      const dept = departments.find((d) => d.DepartmentID === id);
                      return dept?.Name;
                    }).join(', ')
                  : 'לא נבחרו'}
              </Typography>

              <Typography variant="body1" color="text.secondary">סוג עובד:</Typography>
              <Typography variant="body1">
                {formData.EmployeeTypeID
                  ? employeeTypes.find((t) => t.TypeID === formData.EmployeeTypeID)?.Name
                  : 'לא נבחר'}
              </Typography>

              <Typography variant="body1" color="text.secondary">סוג שכר:</Typography>
              <Typography variant="body1">
                {formData.WageType === 'monthly' ? 'חודשי' : formData.WageType === 'hourly' ? 'שעתי' : 'יומי'}
              </Typography>

              <Typography variant="body1" color="text.secondary">
                {formData.WageType === 'hourly' ? 'שכר לשעה:' : formData.WageType === 'daily' ? 'שכר ליום:' : 'שכר חודשי:'}
              </Typography>
              <Typography variant="body1">₪{parseFloat(formData.WageAmount || 0).toLocaleString()}</Typography>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>עובד חדש</DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ direction: 'ltr' }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert ref={errorRef} severity="error">{error}</Alert>
            )}

            <Box sx={{ mt: 2, minHeight: 300 }}>
              {renderStepContent(activeStep)}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            ביטול
          </Button>
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={isSubmitting}>
              הקודם
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext}>
              הבא
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'צור עובד'}
            </Button>
          )}
      </DialogActions>
    </MobileDialog>
  );
};

export default EmployeeCreateDialog;
